import React from "react";
import { useNavigate } from "react-router-dom";

import "../../styles/calculator.css";

function formatProjectSummary(project, details) {
  if (typeof details === "string") return details;
  const parts = [];

  if (Array.isArray(details)) {
    for (const item of details) {
      if (!item) continue;
      if (typeof item === "string") {
        parts.push(item);
      } else if (item?.label && item?.value) {
        parts.push(`${item.label}: ${item.value}`);
      } else if (item?.value) {
        parts.push(String(item.value));
      }
    }
  }

  if (parts.length === 0 && project) {
    if (project.name) parts.push(project.name);
    if (project.floors_count) parts.push(`${project.floors_count} floors`);
    if (project.building_use) parts.push(project.building_use);
    if (project.postcode) parts.push(project.postcode);
  }

  if (parts.length === 0) {
    return "No project selected";
  }

  return parts.join(" — ");
}

export function CalculatorSection({ title, description, actions, children, className }) {
  if (!children) return null;

  return (
    <div className={`pd-block${className ? ` ${className}` : ""}`}>
      <div className="pd-section-head">
        <div>
          <h3 className="pd-h3">{title}</h3>
          {description && <p className="pd-section-description">{description}</p>}
        </div>
        {actions && <div className="pd-section-actions">{actions}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function CalculatorLayout({
  title,
  project,
  projectDetails,
  showProjectSummary = true,
  onRun,
  runLabel = "Run calculation",
  runDisabled = false,
  runLoading = false,
  onBack,
  backLabel = "Back",
  costLabel,
  children,
  subtitle,
}) {
  const navigate = useNavigate();
  const summary =
    subtitle !== undefined
      ? subtitle
      : formatProjectSummary(project, projectDetails);
  const shouldShowSummary = showProjectSummary && summary;

  const handleBack = React.useCallback(() => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    navigate(-1);
  }, [navigate, onBack]);

  const handleRun = React.useCallback(() => {
    if (typeof onRun === "function") onRun();
  }, [onRun]);

  const tokensLabel = costLabel || "Included with subscription";

  return (
    <section className="pd-section pd-calculator">
      <div className="pd-container narrow">
        <div className="pd-calculator__header">
          <div>
            <h1 className="pd-h2">{title}</h1>
            {shouldShowSummary && <p className="pd-muted">{summary}</p>}
          </div>
          <div className="pd-actions">
            <span className="pd-token-cost">{tokensLabel}</span>
            <button
              type="button"
              className="pd-btn pd-btn--primary"
              onClick={handleRun}
              disabled={runDisabled || typeof onRun !== "function"}
            >
              {runLoading ? "Running…" : runLabel}
            </button>
            <button type="button" className="pd-btn pd-btn--ghost" onClick={handleBack}>
              {backLabel}
            </button>
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}
